import { api } from '../config/api';

export interface Property {
  id: number;
  name: string;
  address: string | null;
  city: string;
  state: string;
  zip_code: string | null;
  property_type: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  monthly_rent: number | null;
  image_url: string | null;
  is_active: boolean;
  occupancy_status: string | null;
  tenant_name: string | null;
  tenant_email: string | null;
  tenant_phone: string | null;
  lease_start_date: string | null;
  lease_end_date: string | null;
  user_id: number;
  ytd_total: number;
  all_time_total: number;
  last_expense_date: string | null;
  zillow_estimate?: number;
  zillow_url?: string | null;
  redfin_url?: string | null;
  estimated_value?: number | null;
  estimated_rent?: number | null;
  last_value_update?: string | null;
  hoa_url?: string | null;
  hoa_login_url?: string | null;
  hoa_username?: string | null;
  hoa_password?: string | null;
  hoa_notes?: string | null;
  hoa_monthly_fee?: number | null;
  hoa_company_name?: string | null;
  hoa_account_number?: string | null;

  // Payment schedule information
  payment_bank_account?: string | null;
  payment_frequency?: string | null;
  payment_schedule_start_date?: string | null;
  next_payment_date?: string | null;
  payment_details?: string | null;

  // Mortgage information
  mortgage_lender?: string | null;
  mortgage_lender_url?: string | null;
  mortgage_account_number?: string | null;
  mortgage_balance?: number | null;
  mortgage_payment?: number | null;
  mortgage_rate?: number | null;
  mortgage_start_date?: string | null;
  mortgage_maturity_date?: string | null;
  ownership_type?: string | null;
  purchase_price?: number | null;
  purchase_date?: string | null;
  latest_mortgage_statement_id?: number | null;

  // Utility accounts
  cox_account?: string | null;
  cox_monthly?: number | null;
  nv_energy_account?: string | null;
  nv_energy_monthly?: number | null;
  sw_gas_account?: string | null;
  sw_gas_monthly?: number | null;
  water_account?: string | null;
  water_monthly?: number | null;

  created_at: string;
  updated_at: string | null;
}

export interface PropertyWithStats extends Property {
  expense_count: number;
  avg_monthly_expense: number;
  last_30_days_total: number;
}

export interface CreatePropertyDto {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  property_type?: string;
  bedrooms?: number;
  bathrooms?: number;
  square_feet?: number;
  monthly_rent?: number;
  image_url?: string;
  is_active?: boolean;
  occupancy_status?: string;
  tenant_name?: string;
  tenant_email?: string;
  tenant_phone?: string;
  lease_start_date?: string;
  lease_end_date?: string;
  hoa_url?: string;
  hoa_login_url?: string;
  hoa_username?: string;
  hoa_password?: string;
  hoa_notes?: string;
  hoa_monthly_fee?: number;

  // Payment schedule fields
  payment_bank_account?: string;
  payment_frequency?: string;
  payment_schedule_start_date?: string;
  next_payment_date?: string;
  payment_details?: string;

  // Mortgage fields
  mortgage_lender?: string;
  mortgage_lender_url?: string;
  mortgage_account_number?: string;
  mortgage_balance?: number;
  mortgage_payment?: number;
  mortgage_rate?: number;
  mortgage_start_date?: string;
  mortgage_maturity_date?: string;
  ownership_type?: string;
  purchase_price?: number;
  purchase_date?: string;
}

class PropertiesService {
  async getProperties(): Promise<Property[]> {
    const response = await api.get<Property[]>('/properties/');
    return response.data;
  }

  async getProperty(id: number): Promise<PropertyWithStats> {
    const response = await api.get<PropertyWithStats>(`/properties/${id}`);
    return response.data;
  }

  async getPropertyByEmailPrefix(prefix: string): Promise<PropertyWithStats> {
    const response = await api.get<PropertyWithStats>(`/properties/by-email-prefix/${prefix}`);
    return response.data;
  }

  async createProperty(data: CreatePropertyDto): Promise<Property> {
    const response = await api.post<Property>('/properties/', data);
    return response.data;
  }

  async updateProperty(id: number, data: Partial<CreatePropertyDto>): Promise<Property> {
    const response = await api.put<Property>(`/properties/${id}`, data);
    return response.data;
  }

  async deleteProperty(id: number): Promise<void> {
    await api.delete(`/properties/${id}`);
  }
}

export const propertiesService = new PropertiesService();
